import { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useFormContext } from "react-hook-form";
import { Form } from "react-bootstrap";
import { toast } from "react-toastify";

import CardNew from "components/molecules/Card/CardNew";
import testIds from "constants/testIds";
import Button from "components/atoms/Button/Button";
import usePermissions from "hooks/usePermissions";
import errorToast from "helpers/errorToast";
import { postInvoiceRequest, postQuoteRequest, postReportRequest, postShipmentRequest } from "api";
import ReportType from "../../ReportType";
import REPORT_FIELDS from "./ReportFields";
import generatePayload from "./generatePayload";
import PROVIDER_REPORT_FIELDS from "./ProviderReportFields";

const getRequestFn = (selectedReportType: ReportType) => {
  switch (selectedReportType) {
    case ReportType.QUICK_REQUEST:
    case ReportType.REQUESTS:
    case ReportType.CHARGECODES:
      return postReportRequest;
    case ReportType.SHIPMENTS:
      return postShipmentRequest;
    case ReportType.QUOTES:
      return postQuoteRequest;
    case ReportType.INVOICES:
      return postInvoiceRequest;
    default:
      errorToast("Invalid report type");
  }
};

interface ReportFormProps {
  selectedReportType: ReportType;
  onSuccess: () => void;
  onCancel: () => void;
}

function ReportForm({ selectedReportType, onSuccess, onCancel }: ReportFormProps): JSX.Element | null {
  const { handleSubmit, reset } = useFormContext();
  const intl = useIntl();
  const { permissions } = usePermissions();
  const permissionsParsed = JSON.parse(permissions || "{}");
  const co2_emission_enabled = permissionsParsed.company_shipper_settings?.co2_emission_enabled;
  const locations = permissionsParsed.payment_location?.locations;
  const customBrokerTeams = permissionsParsed.custom_broker_teams?.teams;
  const teams = permissionsParsed.provider_teams?.teams;
  const combinedTeams = customBrokerTeams?.concat(teams);
  const shippers = permissionsParsed.analytics_access_shipper_companies?.map((item: any) => ({
    label: item.name,
    value: item.id,
  }));
  const { user_type } = permissionsParsed;

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => reset(), [selectedReportType]);

  const onSubmit = (data: any) => {
    setIsSubmitting(true);

    const payload = generatePayload(selectedReportType, data, user_type);
    if (!payload) {
      setIsSubmitting(false);
      errorToast({});
      return;
    }

    const requestFn = getRequestFn(selectedReportType);
    if (requestFn === undefined) return;
    requestFn(payload)
      .then((res: any) => {
        if (res.status === 200) {
          onSuccess();
          toast(
            intl.formatMessage({
              id: "report.generateReportNotification",
              defaultMessage: "Your report is being generated.This may take up to several minutes.",
            }),
            { type: "success" },
          );
        }
      })
      .catch((error: any) => errorToast(error))
      .finally(() => setIsSubmitting(false));
  };

  const formFields =
    user_type === "shipper"
      ? REPORT_FIELDS(intl, locations, co2_emission_enabled)[selectedReportType]
      : PROVIDER_REPORT_FIELDS(intl, combinedTeams, shippers)[selectedReportType];

  if (!formFields) return null;
  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <CardNew>
        {formFields.map(({ component: FieldComponent, props }, index) => (
          <FieldComponent key={index} {...props} />
        ))}
        <div className="d-flex justify-content-between" style={{ marginTop: "24px" }}>
          <Button onClick={onCancel} variant="outline-primary">
            <FormattedMessage id="general.cancel" defaultMessage="Cancel" />
          </Button>
          <Button
            isLoading={isSubmitting}
            type="submit"
            variant="primary"
            data-testid={testIds.ANALYTICS_GENERATE_REPORT_BUTTON}
          >
            <FormattedMessage id="report.generateReport" defaultMessage="Generate Report" />
          </Button>
        </div>
      </CardNew>
    </Form>
  );
}

export default ReportForm;
